import { openDB } from 'idb';
import type {
  ApiArtist,
  ApiRosterArtist,
  Artist,
  Color,
  MasterDataResponse,
  Session,
  StoredMasterData,
  Style,
  Type
} from '../types/master-data';
import type { Photo } from '../types/photo';

// v8: photos are tagged from the `artists` roster, so cached records carry
// `artists` instead of `catalog_artists`. The upgrade drops the photos store,
// which means a re-sync is required to repopulate it.
export const db = await openDB('photo-db', 8, {
  upgrade(db) {
    // Remove unused parameters since we don't need them
    if (db.objectStoreNames.contains('photos')) {
      db.deleteObjectStore('photos');
    }
    if (db.objectStoreNames.contains('photoBlobs')) {
      db.deleteObjectStore('photoBlobs');
    }

    // Create photos store with auto-incrementing key
    const photoStore = db.createObjectStore('photos', { 
      keyPath: 'id',
      autoIncrement: false  // Use the id from the API
    });

    // Add indices for efficient querying
    photoStore.createIndex('path', 'path', { unique: true });
    photoStore.createIndex('sessions', 'catalog_sessions', { multiEntry: true });
    photoStore.createIndex('colors', 'catalog_colors', { multiEntry: true });
    photoStore.createIndex('styles', 'catalog_styles', { multiEntry: true });
    photoStore.createIndex('artists', 'artists', { multiEntry: true });
    photoStore.createIndex('types', 'catalog_types', { multiEntry: true });

    // Create master data store if it doesn't exist
    if (!db.objectStoreNames.contains('masterData')) {
      const masterStore = db.createObjectStore('masterData', { 
        keyPath: 'id',
        autoIncrement: false 
      });
      masterStore.createIndex('type', 'type', { unique: false });
    }
  }
});

// PHOTOS
export async function getPhoto(id: string): Promise<Photo | undefined> {
  return await db.get('photos', id);
}

export async function isExistingPhoto(id: string): Promise<boolean> {
  const photo = await db.get('photos', id);
  return photo !== undefined;
}

export async function getAllPhotos(): Promise<Photo[]> {
  return db.getAll('photos');
}

export interface SyncProgress {
  completed: number;
  total: number;
  succeeded: number;
  failed: number;
}

export interface PhotoSyncFailure {
  id: number;
  path: string;
  error: string;
}

export interface SyncPhotosResult {
  total: number;
  succeeded: number;
  failed: PhotoSyncFailure[];
}

export interface SyncPhotosOptions {
  concurrency?: number;
  onProgress?: (progress: SyncProgress) => void;
  signal?: AbortSignal;
}

// The sync is latency-bound rather than bandwidth-bound (the /s3 proxy adds
// seconds of TTFB per request), so overlapping requests is what makes it fast.
const DEFAULT_CONCURRENCY = 8;

async function fetchAndStorePhoto(photo: Photo, signal?: AbortSignal) {
  // The photos store keys on `id`; without one the put throws an opaque
  // IndexedDB key-path error that names neither the store nor the record.
  if (photo?.id === undefined || photo?.id === null) {
    console.error('Photo from API has no id:', photo);
    throw new Error(`Photo has no id (path: ${photo?.path ?? 'unknown'})`);
  }

  const cleanPath = photo.path.replace(/^\/+/, '');
  const proxyUrl = `/s3/${cleanPath}`;

  const response = await fetch(proxyUrl, { signal });

  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText} for ${proxyUrl}`);
  }

  const blob = await response.blob();

  const tx = db.transaction('photos', 'readwrite');
  await tx.objectStore('photos').put({
    ...photo,
    blob,
    syncedAt: new Date().toISOString()
  });
  await tx.done;
}

export async function syncPhotos(
  photos: Photo[],
  options: SyncPhotosOptions = {}
): Promise<SyncPhotosResult> {
  const { concurrency = DEFAULT_CONCURRENCY, onProgress, signal } = options;

  const clearTx = db.transaction('photos', 'readwrite');
  await clearTx.objectStore('photos').clear();
  await clearTx.done;

  const total = photos.length;
  const failed: PhotoSyncFailure[] = [];
  let succeeded = 0;
  let next = 0;

  const report = () => {
    onProgress?.({
      completed: succeeded + failed.length,
      total,
      succeeded,
      failed: failed.length
    });
  };

  // Emit 0/total up front so the UI can show the real total immediately.
  report();

  // Each worker pulls the next photo off a shared cursor until the list runs
  // out, so one slow photo stalls only its own worker.
  const worker = async () => {
    while (next < total) {
      if (signal?.aborted) return;

      const photo = photos[next++];

      try {
        await fetchAndStorePhoto(photo, signal);
        succeeded++;
      } catch (photoError) {
        if (signal?.aborted) return;

        const message =
          photoError instanceof Error ? photoError.message : String(photoError);

        failed.push({ id: photo.id, path: photo.path, error: message });
        console.error('Photo sync error:', {
          id: photo.id,
          path: photo.path,
          error: message
        });
      }

      report();
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(concurrency, total) }, worker)
  );

  return { total, succeeded, failed };
}

// Master data functions
export async function saveMasterData(data: StoredMasterData & { type: string }) {
  const tx = db.transaction('masterData', 'readwrite');
  await tx.store.put(data);
  await tx.done;
}

export async function saveMasterDataBulk(dataArray: StoredMasterData[], type: string) {
  // The store keys on `id`; a record without one throws an opaque IndexedDB
  // key-path error that says nothing about which list it came from.
  const keyless = dataArray.filter(data => data?.id === undefined || data?.id === null);
  if (keyless.length > 0) {
    console.error(`Dropping ${keyless.length} ${type} record(s) with no id:`, keyless);
  }

  const tx = db.transaction('masterData', 'readwrite');
  try {
    await Promise.all(
      dataArray
        .filter(data => data?.id !== undefined && data?.id !== null)
        .map(data => tx.store.put({ ...data, type }))
    );
    await tx.done;
  } catch (error) {
    console.error(`Error saving ${type} data:`, error);
    throw error;
  }
}

/**
 * Artists come off the roster endpoint keyed on `artist_id`, but the masterData
 * store keys on `id`. Mirror it across on the way in: a record with no `id`
 * fails the put and aborts the whole sync, photos included.
 *
 * An API still serving the old `catalog_artists` shape sends `id` and no
 * `artist_id`, so take whichever key is present rather than overwriting a good
 * `id` with `undefined`. Both fields end up populated so callers can read
 * either one.
 */
export function normaliseArtists(artists: (ApiArtist | StoredMasterData)[]): Artist[] {
  return artists.flatMap(artist => {
    const key = (artist as ApiArtist).artist_id ?? (artist as StoredMasterData).id;

    if (key === undefined || key === null) {
      console.warn('Skipping artist with no artist_id or id:', artist);
      return [];
    }

    return [{ ...(artist as ApiArtist), id: key, artist_id: key }];
  });
}

/**
 * Which image represents an artist, best first. Several artists have a blank
 * `photo_thumb` upload, so the closeup crop is preferred; it is also a 250x250
 * thumbnail rather than the full-size closeup.
 */
export function artistPortraitPath(
  artist: Pick<ApiRosterArtist, 'photo_closeup_thumb' | 'photo_closeup' | 'photo_thumb'> | undefined
): string | null {
  const path =
    artist?.photo_closeup_thumb || artist?.photo_closeup || artist?.photo_thumb;

  return path ? path.replace(/^\/+/, '') : null;
}

/** The portraits sit in the bucket the photo sync already reads through. */
export function artistPortraitUrl(path: string) {
  return `/s3/${path}`;
}

/**
 * Cache each artist's portrait alongside its record so the summary renders
 * offline, the same way photos carry their own blob. A portrait that fails to
 * download is left out rather than failing the sync: the UI falls back to the
 * network URL, then to the bundled placeholder.
 */
async function withArtistPortraits(artists: Artist[], signal?: AbortSignal): Promise<Artist[]> {
  return Promise.all(
    artists.map(async artist => {
      const path = artistPortraitPath(artist);
      if (!path) return artist;

      try {
        const response = await fetch(artistPortraitUrl(path), { signal });

        if (!response.ok) {
          throw new Error(`${response.status} ${response.statusText}`);
        }

        return { ...artist, portrait: await response.blob() };
      } catch (error) {
        console.warn(
          `Could not cache portrait for ${artist.name} (${path}):`,
          error instanceof Error ? error.message : error
        );
        return artist;
      }
    })
  );
}

export async function syncMasterData(data: MasterDataResponse) {
  try {
    // Clear existing data first
    const clearTx = db.transaction('masterData', 'readwrite');
    await clearTx.store.clear();
    await clearTx.done;

    // Save new data
    await Promise.all([
      saveMasterDataBulk(data.sessions, 'session'),
      saveMasterDataBulk(data.colors, 'color'),
      saveMasterDataBulk(data.styles, 'style'),
      withArtistPortraits(normaliseArtists(data.artists)).then(artists =>
        saveMasterDataBulk(artists, 'artist')
      ),
      saveMasterDataBulk(data.types, 'type')
    ]);
  } catch (error) {
    console.error('Error syncing master data:', error);
    throw error;
  }
}

export async function getMasterDataByType(type: string): Promise<StoredMasterData[]> {
  const tx = db.transaction('masterData', 'readonly');
  const store = tx.objectStore('masterData');
  const allData = await store.getAll();
  return allData.filter(item => item.type === type) as StoredMasterData[];
}

export async function getSessions(): Promise<Session[]> {
  return (await getMasterDataByType('session')) as Session[];
}

export async function getColors(): Promise<Color[]> {
  return (await getMasterDataByType('color')) as Color[];
}

export async function getStyles(): Promise<Style[]> {
  return (await getMasterDataByType('style')) as Style[];
}

export async function getArtists(): Promise<Artist[]> {
  return (await getMasterDataByType('artist')) as Artist[];
}

export async function getTypes(): Promise<Type[]> {
  return (await getMasterDataByType('type')) as Type[];
}

export async function resetDatabase() {
  try {
    // Close current connection
    db.close();
    
    // Delete the database
    await window.indexedDB.deleteDatabase('photo-db');
    
    // Reload the page to reinitialize the database
    window.location.reload();
  } catch (error) {
    console.error('Error resetting database:', error);
    throw error;
  }
}

